import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from './authService';

export const vendorLocationKeys = {
  all: ['vendor-locations'],
  list: () => [...vendorLocationKeys.all, 'list'],
};

export const AREA_TYPE_OPTIONS = [
  { value: 'city', label: 'City / neighborhood' },
  { value: 'county', label: 'County / district' },
  { value: 'state', label: 'State / region' },
  { value: 'country', label: 'Country' },
  { value: 'custom', label: 'Custom area' },
];

const vendorLocationApi = {
  list: async () => {
    const response = await api.get('/vendor/locations');
    return response.data;
  },

  create: async (data) => {
    const response = await api.post('/vendor/locations', data);
    return response.data;
  },

  update: async ({ id, data }) => {
    const response = await api.put(`/vendor/locations/${id}`, data);
    return response.data;
  },

  remove: async (id) => {
    const response = await api.delete(`/vendor/locations/${id}`);
    return response.data;
  },

  toggleStatus: async (id) => {
    const response = await api.put(`/vendor/locations/${id}/toggle-status`);
    return response.data;
  },
};

const buildStatistics = (locations, maxLocations = 25) => {
  const active = locations.filter((l) => l.is_active).length;
  const total = locations.length;
  return {
    total,
    active,
    inactive: total - active,
    remaining: Math.max(0, maxLocations - total),
  };
};

const patchLocationsList = (queryClient, patchFn) => {
  queryClient.setQueryData(vendorLocationKeys.list(), (old) => {
    if (!old?.data) return old;
    const nextData = patchFn(old.data);
    if (!nextData) return old;
    return { ...old, data: nextData };
  });
};

export const useVendorLocations = (options = {}) =>
  useQuery({
    queryKey: vendorLocationKeys.list(),
    queryFn: vendorLocationApi.list,
    ...options,
  });

export const useCreateVendorLocation = (options = {}) => {
  const queryClient = useQueryClient();
  const { onSuccess, ...rest } = options;

  return useMutation({
    mutationFn: vendorLocationApi.create,
    ...rest,
    onSuccess: (response, variables, context) => {
      const newLocation = response?.data;
      if (newLocation) {
        patchLocationsList(queryClient, (data) => {
          const locations = [...(data.locations || []), newLocation];
          return {
            ...data,
            locations,
            statistics: buildStatistics(locations, data.max_locations),
          };
        });
      }
      queryClient.invalidateQueries({ queryKey: vendorLocationKeys.list() });
      onSuccess?.(response, variables, context);
    },
  });
};

export const useUpdateVendorLocation = (options = {}) => {
  const queryClient = useQueryClient();
  const { onSuccess, ...rest } = options;

  return useMutation({
    mutationFn: vendorLocationApi.update,
    ...rest,
    onSuccess: (response, variables, context) => {
      const updated = response?.data;
      if (updated?.id) {
        patchLocationsList(queryClient, (data) => {
          const locations = (data.locations || []).map((loc) =>
            loc.id === updated.id ? updated : loc
          );
          return {
            ...data,
            locations,
            statistics: buildStatistics(locations, data.max_locations),
          };
        });
      }
      queryClient.invalidateQueries({ queryKey: vendorLocationKeys.list() });
      onSuccess?.(response, variables, context);
    },
  });
};

export const useDeleteVendorLocation = (options = {}) => {
  const queryClient = useQueryClient();
  const { onSuccess, onMutate, ...rest } = options;

  return useMutation({
    mutationFn: vendorLocationApi.remove,
    ...rest,
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: vendorLocationKeys.list() });
      const previous = queryClient.getQueryData(vendorLocationKeys.list());
      patchLocationsList(queryClient, (data) => {
        const locations = (data.locations || []).filter((loc) => loc.id !== id);
        return {
          ...data,
          locations,
          statistics: buildStatistics(locations, data.max_locations),
        };
      });
      onMutate?.(id);
      return { previous };
    },
    onError: (_err, _id, context) => {
      if (context?.previous) {
        queryClient.setQueryData(vendorLocationKeys.list(), context.previous);
      }
    },
    onSuccess: (response, variables, context) => {
      queryClient.invalidateQueries({ queryKey: vendorLocationKeys.list() });
      onSuccess?.(response, variables, context);
    },
  });
};

export const useToggleVendorLocationStatus = (options = {}) => {
  const queryClient = useQueryClient();
  const { onSuccess, ...rest } = options;

  return useMutation({
    mutationFn: vendorLocationApi.toggleStatus,
    ...rest,
    onSuccess: (response, variables, context) => {
      const updated = response?.data;
      if (updated?.id) {
        patchLocationsList(queryClient, (data) => {
          const locations = (data.locations || []).map((loc) =>
            loc.id === updated.id ? updated : loc
          );
          return {
            ...data,
            locations,
            statistics: buildStatistics(locations, data.max_locations),
          };
        });
      }
      queryClient.invalidateQueries({ queryKey: vendorLocationKeys.list() });
      onSuccess?.(response, variables, context);
    },
  });
};

export const formatLocationLabel = (location) => {
  if (!location) return '';
  const geo = [location.city, location.state, location.country].filter(Boolean).join(', ');
  return geo || location.area_name || 'Unnamed location';
};

export const locationToForm = (location = null) => ({
  area_name: location?.area_name || '',
  area_type: location?.area_type || 'city',
  city: location?.city || '',
  state: location?.state || '',
  country: location?.country || '',
  address_line: location?.address_line || '',
  latitude: location?.coordinates?.lat ?? '',
  longitude: location?.coordinates?.lng ?? '',
  travel_fee: location?.travel_fee ?? '',
  travel_time_minutes: location?.travel_time_minutes ?? '',
  notes: location?.notes || '',
  is_active: location?.is_active !== false,
});

export default vendorLocationApi;
